import { useQuery } from "@tanstack/react-query"
import ResponseError from "../../../types/responseError"
import { getContentfulClient, TaxonomyConcept } from "../../../lib/contentful/contentfulClient"
import { getHeap } from "../../../utils/heap"
import buildCarrotApiRetryPolicy from "../../../utils/CarrotApiRetryPolicy"
import { useIntl } from "react-intl"
import { getContentfulLocale } from "../../../lib/contentful/utils/contentfulLocaleUtils"
import { TypeCategory, TypeCategorySkeleton, TypeSubCategory } from "#/types/contentful"

const client = getContentfulClient()
const ENDPOINT = "GetCategories"
const CATEGORIES_PER_REQUEST = 5

type CategoryEntry = TypeCategory<"WITHOUT_UNRESOLVABLE_LINKS">

type SubCategoryEntry = TypeSubCategory<"WITHOUT_UNRESOLVABLE_LINKS">

const hasCarrotAcademyTaxonomy = (entry: SubCategoryEntry) =>
  entry?.metadata?.concepts?.some((concept) => concept.sys.id === TaxonomyConcept.CARROT_ACADEMY)

const filterSubcategoriesByTaxonomy = (categories: CategoryEntry[]): CategoryEntry[] =>
  categories.map((category) => ({
    ...category,
    fields: {
      ...category.fields,
      subCategories: category.fields.subCategories?.filter(hasCarrotAcademyTaxonomy)
    }
  }))

const listCategoryIdsForCarrotAcademy = async (locale: string) => {
  const { items } = await client.withoutUnresolvableLinks.getEntries<TypeCategorySkeleton>({
    content_type: "category",
    include: 0,
    locale: getContentfulLocale(locale),
    select: ["sys"],
    "metadata.concepts.sys.id[in]": [TaxonomyConcept.CARROT_ACADEMY]
  })
  return items.map((entry) => entry.sys.id)
}

const loadCategoriesBatch = async (categoryIds: string[], locale: string) => {
  const { items } = await client.withoutUnresolvableLinks.getEntries<TypeCategorySkeleton>({
    content_type: "category",
    include: 10,
    locale: getContentfulLocale(locale),
    "sys.id[in]": categoryIds
  })
  return items
}

const loadCategories = async (locale: string) => {
  const beforeFetch = performance.now()

  const categoryIds = await listCategoryIdsForCarrotAcademy(locale)
  const batches: string[][] = []
  for (let i = 0; i < categoryIds.length; i += CATEGORIES_PER_REQUEST) {
    batches.push(categoryIds.slice(i, i + CATEGORIES_PER_REQUEST))
  }
  const responses = await Promise.all(batches.map((batch) => loadCategoriesBatch(batch, locale)))
  const items = responses.flat()

  const afterFetch = performance.now()

  getHeap().track("Contentful Category Load Time", {
    "Library Categories Load Time In Milliseconds": afterFetch - beforeFetch
  })

  return filterSubcategoriesByTaxonomy(items)
}

const getCategoriesCacheKey = (locale: string): (string | number)[] => [ENDPOINT, locale]

export default function useGetCategories() {
  const intl = useIntl()
  const { locale } = intl

  return useQuery<CategoryEntry[], ResponseError>({
    queryKey: getCategoriesCacheKey(locale),
    queryFn: () => loadCategories(locale),
    refetchOnWindowFocus: false,
    retry: buildCarrotApiRetryPolicy(3),
    staleTime: 1000 * 60 * 5 // 5 minutes
  })
}
